/**
 * État du miroir CRM côté PWA : lecture, index, et mutations optimistes.
 *
 * Modèle de chargement : tout, une fois. 1 800 sociétés, 2 300 liens et
 * 2 100 liens d'étiquette tiennent en quelques centaines de ko une fois les
 * colonnes réduites à ce que l'écran affiche. Charger la totalité rend le
 * tri, la recherche et les compteurs de files instantanés et hors ligne —
 * ce qui est précisément le cas d'usage (canapé, salle d'attente).
 *
 * Écriture : optimiste. L'état local change au tap, la file de `write.ts`
 * se charge d'atteindre Supabase quand elle peut. Le Mac ramasse au pull
 * suivant via son curseur `server_seq`.
 */

import { get, set } from "idb-keyval";
import { supabase } from "./client";
import { patch, upsert, tombstone, revive, nowIso } from "./write.svelte";
import {
  completeness,
  classificationOf,
  isNoise,
  needsTier,
  type Slot,
} from "./completeness";
import type { Company, CrmTag, CrmVocab, Link, TagLink, Tier } from "./types";

const CACHE_KEY = "mailfox:crm-cache";
const PAGE = 1000; // PostgREST plafonne les réponses ; on pagine explicitement

/**
 * Étiquettes de service créées par des passes d'import côté Mac. Elles
 * polluent la grille sans rien vouloir dire. Retire ce filtre si tu as
 * besoin de les voir.
 */
const HIDDEN_TAG = /^MAJ[\s_]/i;

export type QueueId =
  | "group"
  | "role"
  | "profile"
  | "buyer"
  | "seller"
  | "country"
  | "incomplete"
  | "all";

export const QUEUES: Array<{ id: QueueId; label: string }> = [
  { id: "group", label: "Sans groupe" },
  { id: "role", label: "Sans rôle" },
  { id: "profile", label: "Sans profil" },
  { id: "buyer", label: "Sans acheteur" },
  { id: "seller", label: "Sans vendeur" },
  { id: "country", label: "Sans pays" },
  { id: "incomplete", label: "Incomplet" },
  { id: "all", label: "Tout" },
];

const EMPTY_VOCAB: CrmVocab = {
  classifications: [],
  buyerProfiles: [],
  functions: [],
  tiers: [],
};

export const crmState = $state<{
  loading: boolean;
  hydrated: boolean;
  error: string | null;
  vocab: CrmVocab;
  tags: CrmTag[];
  companies: Company[];
  links: Link[];
  tagLinks: TagLink[];
  /** company_uid → liens, reconstruit par `reindex()`. */
  linksBy: Record<string, Link[]>;
  /** company_uid → tag_uid[], reconstruit par `reindex()`. */
  tagsBy: Record<string, string[]>;
  queue: QueueId;
  /**
   * Second axe de filtrage, indépendant de la file : code ISO, ou `null`
   * pour tous les pays. Croiser les deux est l'usage réel — « sans groupe,
   * en France » n'est pas la même corvée que « sans groupe, en Allemagne ».
   */
  country: string | null;
  query: string;
  selectedUid: string | null;
}>({
  loading: false,
  hydrated: false,
  error: null,
  vocab: EMPTY_VOCAB,
  tags: [],
  companies: [],
  links: [],
  tagLinks: [],
  linksBy: {},
  tagsBy: {},
  queue: "group",
  country: null,
  query: "",
  selectedUid: null,
});

// ─── Index ─────────────────────────────────────────────────────────────────

/**
 * Les listes se rendent jusqu'à 1 800 lignes, chacune ayant besoin de son
 * nombre d'étiquettes et de fonctions. Sans index, ce serait un balayage des
 * 4 400 liens par ligne rendue.
 */
export function reindex(): void {
  const linksBy: Record<string, Link[]> = {};
  for (const l of crmState.links) {
    (linksBy[l.company_uid] ??= []).push(l);
  }
  const tagsBy: Record<string, string[]> = {};
  for (const t of crmState.tagLinks) {
    (tagsBy[t.entity_uid] ??= []).push(t.tag_uid);
  }
  crmState.linksBy = linksBy;
  crmState.tagsBy = tagsBy;
}

export function linksOf(companyUid: string): Link[] {
  return crmState.linksBy[companyUid] ?? [];
}

export function tagUidsOf(companyUid: string): string[] {
  return crmState.tagsBy[companyUid] ?? [];
}

/** Un contact de cette société porte-t-il cette fonction ? */
export function hasFunction(companyUid: string, fn: string): boolean {
  return linksOf(companyUid).some((l) => (l.functions ?? []).includes(fn));
}

/** Nombre de liens portant au moins une fonction — la case « organigramme ». */
export function functionCountOf(companyUid: string): number {
  return linksOf(companyUid).filter((l) => (l.functions?.length ?? 0) > 0).length;
}

export function completenessOf(c: Company) {
  return completeness(c, tagUidsOf(c.uid).length, functionCountOf(c.uid));
}

export function tierOf(code: string | null): Tier | null {
  if (!code) return null;
  return crmState.vocab.tiers.find((t) => t.code === code) ?? null;
}

export function companyByUid(uid: string | null): Company | null {
  if (!uid) return null;
  return crmState.companies.find((c) => c.uid === uid) ?? null;
}

// ─── Chargement ────────────────────────────────────────────────────────────

async function fetchAll<T>(
  table: string,
  select: string,
  tweak?: (q: any) => any,
): Promise<T[]> {
  const out: T[] = [];
  for (let from = 0; ; from += PAGE) {
    let q = supabase.from(table).select(select).is("deleted_at", null);
    if (tweak) q = tweak(q);
    const { data, error } = await q.range(from, from + PAGE - 1);
    if (error) throw new Error(`${table}: ${error.message}`);
    const rows = (data ?? []) as unknown as T[];
    out.push(...rows);
    if (rows.length < PAGE) return out;
  }
}

function parseVocab(rows: Array<{ key: string; value: unknown }>): CrmVocab {
  const by = new Map(rows.map((r) => [r.key, r.value]));
  const arr = (k: string): string[] => {
    const v = by.get(k);
    return Array.isArray(v) ? (v as string[]) : [];
  };
  const tiers = by.get("tiers");
  return {
    classifications: arr("company_classifications"),
    buyerProfiles: arr("buyer_profiles"),
    functions: arr("contact_functions"),
    tiers: Array.isArray(tiers) ? (tiers as Tier[]) : [],
  };
}

const COMPANY_COLS =
  "uid,name,domain,city,country_code,website,naf_label,revenue,employees,notes,archived,created_at,classification,buyer_profile,tier,cadence_days,reviewed_at";

const LINK_COLS =
  "contact_uid,company_uid,is_primary,active,role,functions,created_at," +
  "crm_contacts!inner(uid,email,display_name,first_name,last_name,role,phone)";

interface CacheShape {
  vocab: CrmVocab;
  tags: CrmTag[];
  companies: Company[];
  links: Link[];
  tagLinks: TagLink[];
  cachedAt: number;
}

/** Cache IDB — l'écran s'ouvre plein, même hors ligne, puis se rafraîchit. */
export async function hydrateFromCache(): Promise<void> {
  if (crmState.hydrated) return;
  try {
    const c = await get<CacheShape>(CACHE_KEY);
    if (!c) return;
    crmState.vocab = c.vocab;
    crmState.tags = c.tags;
    crmState.companies = c.companies;
    crmState.links = c.links;
    crmState.tagLinks = c.tagLinks;
    crmState.hydrated = true;
    reindex();
  } catch (e) {
    console.warn("crm: cache read failed:", e);
  }
}

async function saveCache(): Promise<void> {
  try {
    await set(CACHE_KEY, {
      vocab: $state.snapshot(crmState.vocab),
      tags: $state.snapshot(crmState.tags),
      companies: $state.snapshot(crmState.companies),
      links: $state.snapshot(crmState.links),
      tagLinks: $state.snapshot(crmState.tagLinks),
      cachedAt: Date.now(),
    } satisfies CacheShape);
  } catch (e) {
    console.warn("crm: cache write failed:", e);
  }
}

export async function loadCrm(): Promise<void> {
  if (crmState.loading) return;
  crmState.loading = true;
  crmState.error = null;
  try {
    const [settings, tags, companies, links, tagLinks] = await Promise.all([
      fetchAll<{ key: string; value: unknown }>("crm_settings", "key,value"),
      fetchAll<CrmTag>("crm_tags", "uid,name,color,description"),
      fetchAll<Company>("crm_companies", COMPANY_COLS),
      fetchAll<Link & { crm_contacts: Link["contact"] }>(
        "crm_contact_companies",
        LINK_COLS,
        (q) => q.is("crm_contacts.deleted_at", null),
      ),
      fetchAll<TagLink>("crm_tag_links", "tag_uid,entity_uid,created_at", (q) =>
        q.eq("entity_type", "company"),
      ),
    ]);

    crmState.vocab = parseVocab(settings);
    crmState.tags = tags
      .filter((t) => !HIDDEN_TAG.test(t.name))
      .sort((a, b) => a.name.localeCompare(b.name, "fr"));
    crmState.companies = companies.sort((a, b) =>
      a.name.localeCompare(b.name, "fr"),
    );
    // L'embed PostgREST arrive sous le nom de la table ; on l'aplatit en
    // `contact` pour que le reste du code ignore d'où il vient.
    crmState.links = links.map((l) => ({ ...l, contact: l.crm_contacts ?? null }));
    crmState.tagLinks = tagLinks;
    crmState.hydrated = true;
    reindex();
    void saveCache();
  } catch (e) {
    crmState.error = e instanceof Error ? e.message : String(e);
  } finally {
    crmState.loading = false;
  }
}

// ─── Files & recherche ─────────────────────────────────────────────────────

function matchesQuery(c: Company, q: string): boolean {
  if (!q) return true;
  const hay =
    `${c.name} ${c.domain ?? ""} ${c.city ?? ""} ${c.country_code ?? ""}`.toLowerCase();
  return hay.includes(q);
}

function inQueue(c: Company, queue: QueueId): boolean {
  if (queue === "all") return true;
  // Classée à la main = sortie de toutes les files, quelles que soient les
  // cases vides. C'est la raison d'être du geste.
  if (c.reviewed_at) return false;
  const cls = classificationOf(c);
  switch (queue) {
    case "group":
      // Même règle que la complétude : seules les relations qu'on relance
      // ont besoin d'un groupe. Sinon la file se remplit d'administrations
      // qu'on n'a aucune raison de cadencer.
      return needsTier(c) && !c.tier;
    case "role":
      return cls.length === 0;
    case "profile":
      return !c.buyer_profile && (cls.includes("customer") || cls.includes("intermediary"));
    // Les deux files d'organigramme ne visent QUE les sociétés qui ont déjà
    // des contacts : sans contact, il n'y a pas de fonction à poser, et la
    // fiche resterait dans la file sans qu'aucun geste ne l'en sorte. Le
    // manque de contacts est un autre problème, qui se règle sur le Mac.
    case "buyer":
      // « Acheteur » = celui qui reçoit mes offres. La question ne se pose
      // que chez qui achète.
      return (
        (cls.includes("customer") || cls.includes("intermediary")) &&
        linksOf(c.uid).length > 0 &&
        !hasFunction(c.uid, "buyer")
      );
    case "seller":
      // « Vendeur » = celui qui m'envoie ses stocks.
      return (
        (cls.includes("supplier") || cls.includes("intermediary")) &&
        linksOf(c.uid).length > 0 &&
        !hasFunction(c.uid, "seller")
      );
    case "country":
      // Le pays est un renseignement, pas une décision : il ne compte pas
      // dans la complétude, mais il a sa file pour être rattrapé en lot.
      return !c.country_code && !isNoise(c);
    case "incomplete":
      return !completenessOf(c).done;
  }
}

/**
 * Ordre de la file. Sans les signaux d'engagement — qui restent locaux au
 * Mac et ne traversent jamais le miroir — le meilleur proxy disponible est
 * la surface de la relation : nombre de contacts, puis présence d'un
 * domaine (un `@gmail.com` est plus souvent un indépendant qu'un compte
 * machine), puis le nom pour que l'ordre soit stable d'une session à
 * l'autre.
 */
function rank(c: Company): number {
  return linksOf(c.uid).length * 2 + (c.domain ? 1 : 0);
}

function inCountry(c: Company, code: string | null): boolean {
  return code === null || c.country_code === code;
}

export function visibleCompanies(): Company[] {
  const q = crmState.query.trim().toLowerCase();
  const queue = crmState.queue;
  // Une recherche explicite cherche dans TOUT — file et pays compris —
  // sinon on ne trouve jamais la société qu'on a en tête parce qu'elle est
  // déjà classée ou dans un autre pays.
  const pool = q
    ? crmState.companies
    : crmState.companies.filter((c) => !c.archived);
  return pool
    .filter((c) =>
      q ? matchesQuery(c, q) : inQueue(c, queue) && inCountry(c, crmState.country),
    )
    .sort((a, b) => rank(b) - rank(a) || a.name.localeCompare(b.name, "fr"));
}

/** Compte d'une file DANS le pays actif — les deux axes se croisent. */
export function queueCount(queue: QueueId): number {
  return crmState.companies.filter(
    (c) => !c.archived && inQueue(c, queue) && inCountry(c, crmState.country),
  ).length;
}

export interface CountryTally {
  code: string;
  count: number;
}

/**
 * Les pays présents DANS la file active, du plus fourni au moins fourni.
 * Compter sur la file plutôt que sur tout le portefeuille est ce qui rend
 * la barre utile : elle dit où reste le travail, pas où sont les sociétés.
 */
export function countryTallies(): CountryTally[] {
  const by = new Map<string, number>();
  for (const c of crmState.companies) {
    if (c.archived || !c.country_code) continue;
    if (!inQueue(c, crmState.queue)) continue;
    by.set(c.country_code, (by.get(c.country_code) ?? 0) + 1);
  }
  return [...by.entries()]
    .map(([code, count]) => ({ code, count }))
    .sort((a, b) => b.count - a.count || a.code.localeCompare(b.code));
}

/** Total de la file, tous pays — le compte du chip « Tous ». */
export function queueTotal(): number {
  return crmState.companies.filter(
    (c) => !c.archived && inQueue(c, crmState.queue),
  ).length;
}

// ─── Mutations ─────────────────────────────────────────────────────────────

const COMPANIES = "crm_companies";
const LINKS = "crm_contact_companies";
const TAG_LINKS = "crm_tag_links";

function mutate(uid: string, values: Partial<Company>): void {
  const c = companyByUid(uid);
  if (!c) return;
  Object.assign(c, values);
  patch(COMPANIES, { uid }, values as Record<string, unknown>);
}

/** Le rôle est multi-valué. Un tableau vide se range en NULL, comme en base. */
export function toggleClassification(uid: string, value: string): void {
  const c = companyByUid(uid);
  if (!c) return;
  const cur = classificationOf(c);
  const next = cur.includes(value)
    ? cur.filter((v) => v !== value)
    : [...cur, value];
  mutate(uid, { classification: next.length > 0 ? next : null });
}

/**
 * « Bruit » remplace le rôle au lieu de s'y ajouter : une société qui est du
 * bruit n'est pas aussi un client. Et on retire le profil et le groupe, qui
 * n'ont plus de sens — sinon la fiche resterait comptée comme à revoir.
 */
export function markNoise(uid: string): void {
  mutate(uid, { classification: ["noise"], buyer_profile: null, tier: null });
}

export function setBuyerProfile(uid: string, value: string): void {
  const c = companyByUid(uid);
  if (!c) return;
  mutate(uid, { buyer_profile: c.buyer_profile === value ? null : value });
}

export function setTier(uid: string, code: string): void {
  const c = companyByUid(uid);
  if (!c) return;
  mutate(uid, { tier: c.tier === code ? null : code });
}

/** Affectation directe — c'est l'appelant qui décide d'un éventuel toggle. */
export function setCountry(uid: string, code: string | null): void {
  mutate(uid, { country_code: code });
}

/**
 * La note libre de la société — le champ `notes` que la fiche du Mac affiche
 * déjà et laisse éditer. À ne pas confondre avec la table `crm_notes` du
 * CRM v2 : elle existe en base des deux côtés mais aucune UI ne la lit
 * encore, une note écrite là serait invisible sur le Mac.
 *
 * L'appelant se charge de temporiser la frappe ; la file d'écriture fusionne
 * de toute façon les appels successifs sur la même société.
 */
export function setNotes(uid: string, text: string): void {
  const trimmed = text.trim();
  mutate(uid, { notes: trimmed.length > 0 ? trimmed : null });
}

/**
 * Déclare la fiche classée (ou annule). Le geste dit « je n'ai plus rien à
 * saisir ici », pas « tout est rempli » : c'est la sortie de secours des
 * fiches que la règle de complétude ne saura jamais satisfaire.
 */
export function setReviewed(uid: string, on: boolean): void {
  mutate(uid, { reviewed_at: on ? nowIso() : null });
}

export function setArchived(uid: string, archived: boolean): void {
  mutate(uid, { archived });
}

/**
 * Suppression : une pierre tombale, qui se propagera au Mac et y effacera la
 * fiche et ses liens. Réversible tant que `undoDeleteCompany` est appelé —
 * au-delà, seule une réécriture depuis le Mac la ramènerait.
 */
export function deleteCompany(uid: string): void {
  crmState.companies = crmState.companies.filter((c) => c.uid !== uid);
  tombstone(COMPANIES, { uid });
}

export function undoDeleteCompany(c: Company): void {
  crmState.companies = [...crmState.companies, c].sort((a, b) =>
    a.name.localeCompare(b.name, "fr"),
  );
  revive(COMPANIES, { uid: c.uid });
}

export function toggleTag(companyUid: string, tagUid: string): void {
  const existing = crmState.tagLinks.find(
    (t) => t.entity_uid === companyUid && t.tag_uid === tagUid,
  );
  if (existing) {
    crmState.tagLinks = crmState.tagLinks.filter((t) => t !== existing);
    // Le lien n'a pas d'`uid` : sa clé naturelle est le triplet, d'où
    // l'upsert plutôt qu'un update — la ligne peut avoir été purgée.
    upsert(TAG_LINKS, "tag_uid,entity_type,entity_uid", {
      tag_uid: tagUid,
      entity_type: "company",
      entity_uid: companyUid,
      created_at: existing.created_at,
      deleted_at: nowIso(),
    });
  } else {
    const created_at = nowIso();
    crmState.tagLinks = [
      ...crmState.tagLinks,
      { tag_uid: tagUid, entity_uid: companyUid, created_at },
    ];
    upsert(TAG_LINKS, "tag_uid,entity_type,entity_uid", {
      tag_uid: tagUid,
      entity_type: "company",
      entity_uid: companyUid,
      created_at,
      deleted_at: null,
    });
  }
  reindex();
}

/** Les fonctions vivent sur l'arête contact ↔ société, jamais sur le contact. */
export function toggleFunction(
  companyUid: string,
  contactUid: string,
  fn: string,
): void {
  const link = crmState.links.find(
    (l) => l.company_uid === companyUid && l.contact_uid === contactUid,
  );
  if (!link) return;
  const cur = link.functions ?? [];
  const next = cur.includes(fn) ? cur.filter((v) => v !== fn) : [...cur, fn];
  link.functions = next.length > 0 ? next : null;
  patch(
    LINKS,
    { company_uid: companyUid, contact_uid: contactUid },
    { functions: link.functions },
  );
  reindex();
}

export type { Slot };
